import { useMemo, useEffect } from 'react';
import { DATE_FORMAT, TIME_FORMAT } from '../services/timezone-service';
import { FALLBACK_FORMAT_PARAMS } from '../utilities/timezone-utilities';
import currentPortalStore from '../stores/current-portal-store';

/**
 * Override options for date/time formatting parameters.
 * Any provided value takes precedence over merchant settings or fallback defaults.
 */
export interface IFormattingOverrides {
    /** Override timezone (e.g., 'UTC' for billing, or browser timezone for calendar preview) */
    timeZone?: string;
    
    /** Override date format (e.g., DATE_FORMAT.FOURTH for specific display requirements) */
    dateFormat?: DATE_FORMAT;
    
    /** Override time format (12h vs 24h) */
    timeFormat?: TIME_FORMAT;
}

/**
 * Result of useDateTimeFormattingParams hook.
 * Contains all parameters needed for consistent date/time formatting.
 */
export interface IFormattingParams {
    timeZone: string;
    dateFormat: DATE_FORMAT;
    timeFormat: TIME_FORMAT;
}

/**
 * Hook for getting date/time formatting parameters with automatic fallback logic.
 * 
 * Priority order:
 * 1. Explicit overrides (passed as parameter)
 * 2. Merchant timezone store settings (if TIMEZONES feature enabled)
 * 3. Fallback defaults (UTC, DATE_FORMAT.FIRST, TIME_FORMAT.AM_PM)
 * 
 * @param overrides - Optional overrides for any formatting parameter
 * @returns Formatting parameters ready to use with DateTimeV2 or date utilities
 * 
 * @example
 * // Standard usage - uses merchant settings or fallback
 * const params = useDateTimeFormattingParams();
 * <DateTimeV2 {...params} utcDateString={date} />
 * 
 * @example
 * // Override timezone for billing (always UTC)
 * const params = useDateTimeFormattingParams({ timeZone: 'UTC' });
 * <DateTimeV2 {...params} utcDateString={transaction.created_at} />
 * 
 * @example
 * // Override timezone for calendar preview (browser timezone)
 * const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
 * const params = useDateTimeFormattingParams({ timeZone: browserTz });
 * <DateTimeV2 {...params} date={selectedDate} />
 * 
 * @example
 * // Get only timezone for DateRangePickerV2
 * const { timeZone } = useDateTimeFormattingParams();
 * <DateRangePickerV2 timeZone={timeZone} />
 * 
 * @example
 * // Override multiple parameters
 * const params = useDateTimeFormattingParams({ 
 *     timeZone: 'UTC',
 *     dateFormat: DATE_FORMAT.FOURTH,
 *     timeFormat: TIME_FORMAT.TWENTY_FOUR
 * });
 */
export const useDateTimeFormattingParams = (overrides?: IFormattingOverrides): IFormattingParams => {
    
    /** Ensure CurrentPortalStore is loaded before accessing settings */
    useEffect(() => {
        currentPortalStore.load();
    }, []);

    const tzStore = currentPortalStore.setting?.timezoneStore;
    
    /** Memoize to prevent unnecessary re-renders */
    return useMemo(() => {
        /** Determine base parameters (merchant settings or fallback) */
        const baseParams = tzStore ? {
            timeZone: tzStore.timezone,
            dateFormat: tzStore.dateFormat,
            timeFormat: tzStore.timeFormat,
        } : {
            timeZone: FALLBACK_FORMAT_PARAMS.timeZone,
            dateFormat: FALLBACK_FORMAT_PARAMS.dateFormat,
            timeFormat: FALLBACK_FORMAT_PARAMS.timeFormat,
        };
        
        /** Apply overrides if provided */
        return {
            timeZone: overrides?.timeZone ?? baseParams.timeZone,
            dateFormat: overrides?.dateFormat ?? baseParams.dateFormat,
            timeFormat: overrides?.timeFormat ?? baseParams.timeFormat,
        };
    }, [tzStore, overrides]);
};

